import { ImSys } from '../../core/ImSys.js';

// src/view/transport/ServerExecutor.ts
/**
 * Базовый транспортный исполнитель:
 *  - собирает payload из ServerRequest и TransportContext,
 *  - выполняет POST через ImSys.http.post,
 *  - сообщает результат через хуки onResponse/onError,
 *  - управляет занятостью через onBusy.
 *
 * Вся логика разбора массива команд (UI/ServerCmd адаптеры) — на стороне клиента.
 */
class ServerExecutor {
    /** Выполняет сетевой вызов по протоколу и сообщает результаты через хуки. */
    async execute(req, ctx, hooks) {
        hooks?.onBusy?.(true);
        try {
            const context = {
                siteKey: ctx.siteKey,
                pageKey: ctx.pageKey,
                viewConfig: ctx.viewConfig,
                lang: ctx.lang,
                uiLang: ctx.uiLang,
                location: ctx.location
            };
            context.uiLang ||= context.lang;
            const payload = this._buildPayload(req, context);
            let json;
            try {
                // Выполнение запроса
                json = await this._postRequest(ImSys.cmdHost + window.location.search, payload);
            }
            catch (err) {
                console.error(err);
                hooks?.onError?.({ result: "error", type: "system" });
                return;
            }
            // Контракт ответа:
            //  result === "ok"        → успешный сценарий, команды в json.response
            //  result !== "ok"/ошибка → возможны команды в json.response + сообщение json.msg
            if (json?.result === "ok") {
                const cmds = json.response ?? [];
                hooks?.onResponse?.(cmds);
            }
            else {
                const cmds = json?.response;
                hooks?.onError?.(json, cmds);
            }
        }
        finally {
            hooks?.onBusy?.(false);
        }
    }
    /** Собирает payload для POST на сервер из запроса и готового контекста. */
    _buildPayload(req, ctx) {
        return {
            ...ctx,
            requestData: JSON.stringify(req)
        };
    }
    /**
     * POST как application/x-www-form-urlencoded (аналог jQuery $.post).
     * Отправляет cookies (credentials: "include").
     * При успехе парсит JSON по Content-Type
     *
     * @param url  адрес запроса
     * @param body поля формы (siteKey, pageKey, viewConfig, lang, uiLang, requestData, location)
     * @throws Error при HTTP ≥ 400 или сетевой ошибке
     */
    async _postRequest(url, body) {
        const antiforgeryToken = ServerExecutor._readAntiforgeryToken();
        const params = new URLSearchParams();
        for (const [k, v] of Object.entries(body)) {
            params.append(k, v == null ? "" : String(v));
        }
        const res = await fetch(url /* imsys.cmdUrl + window.location.search */, {
            method: "POST",
            headers: {
                "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
                "X-Requested-With": "XMLHttpRequest",
                [ServerExecutor._antiforgeryHeaderName]: antiforgeryToken,
                "Accept": "application/json, text/javascript, */*; q=0.01"
            },
            body: params.toString(),
            credentials: "include", // как у jQuery — куки уедут даже при другом хосте
            redirect: "follow"
        });
        if (!res.ok) {
            const text = await res.text().catch(() => "");
            throw new Error(`HTTP ${res.status} ${res.statusText} ${text}`);
        }
        // На всякий случай поддержим ответ text→JSON, как иногда делает бэкенд.
        const ct = res.headers.get("content-type") || "";
        if (ct.includes("application/json"))
            return res.json();
        const text = await res.text();
        return JSON.parse(text);
    }
    static _readAntiforgeryToken() {
        const token = document.querySelector(ServerExecutor._antiforgeryTokenSelector)?.content;
        if (!token) {
            throw new Error("Request verification token is missing.");
        }
        return token;
    }
}
ServerExecutor._antiforgeryTokenSelector = 'meta[name="request-verification-token"]';
ServerExecutor._antiforgeryHeaderName = "RequestVerificationToken";

export { ServerExecutor };
